import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect
} from 'react';
import { API_URLS } from '../../Services/servicioURL';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';

import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

// Interfaces
interface Tecnico {
  id: number;
  nombre: string;
  usuario: string;
}

interface Empleado {
  cveemp: string;
  nomemp: string;
}

interface PeticionType {
  folio: number;
  cveemp: string;
  fecha_hora: string;
  titulo: string;
  justificacion: string;
  sucursal: string;
  departamento: string;
  estatus: string;
  confirmado?: boolean;
  fechapromesa?: string | null;
  motivo?: string | null;
  tecnico_id?: number | null;
  tecnicoAsignado?: Tecnico | null;
  empleado?: Empleado;
  imagen_url?: string | null;
}

// Constants optimizados para táctil y modo oscuro
const MAZATLAN_TIMEZONE = 'America/Mazatlan';
const POLLING_INTERVAL = 15000; // 15 segundos para táctil
const MIN_TOUCH_TARGET = 48; // Mínimo 48px para elementos táctiles

// COLORES MODO OSCURO - Paleta moderna y elegante
const DARK_COLORS = {
  // Fondos principales
  primaryBg: '#0f0f23',
  secondaryBg: '#1a1a2e',
  cardBg: '#16213e',
  modalBg: '#1a1a2e',
  
  // Fondos de elementos
  elementBg: '#252545',
  hoverBg: '#2d2d50',
  activeBg: '#353560',
  
  // Textos
  primaryText: '#e2e8f0',
  secondaryText: '#ffffffff',
  mutedText: '#ffffffff',
  
  // Bordes
  border: '#334155',
  borderLight: '#475569',
  
  // Acentos
  accent: '#6366f1',
  accentHover: '#4f46e5',
  
  // Estados
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#050505ff',
  info: '#06b6d4'
};

// Solo mostrar PENDIENTE y TRABAJANDO con colores oscuros
const ESTATUS_CONFIG = {
  PENDIENTE: {
    color: '#f59e0b',
    icon: '⏳',
    label: 'PENDIENTE',
    order: 1,
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    darkGradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
  },
  TRABAJANDO: {
    color: '#10b981',
    icon: '🔧',
    label: 'TRABAJANDO',
    order: 2,
    gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    darkGradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
  }
};

// Utility functions
const formatDateTime = (date: string): string => {
  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return '-';
    
    return dateObj.toLocaleString('es-ES', {
      timeZone: MAZATLAN_TIMEZONE,
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit', 
      minute: '2-digit'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '-';
  }
};

const getEstatusReal = (estatus: string, confirmado: boolean): string => {
  return confirmado ? 'CONFIRMADO' : estatus;
};

const formatTimeAgo = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Ahora';
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d`;
  } catch (error) {
    return 'N/A';
  }
};

// Main component
const PeticionesPanel: React.FC = () => {
  const toast = useRef<Toast>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // State
  const [peticiones, setPeticiones] = useState<PeticionType[]>([]);
  const [tecnicos, setTecnicos] = useState<Tecnico[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [selectedPeticion, setSelectedPeticion] = useState<PeticionType | null>(null);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [imagenModalVisible, setImagenModalVisible] = useState<boolean>(false);
  const [imagenSeleccionada, setImagenSeleccionada] = useState<string>('');

  // API calls
  const fetchWithErrorHandling = useCallback(async (url: string, options?: RequestInit) => {
    try {
      const response = await fetch(url, options);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`Error fetching ${url}:`, error);
      throw error;
    }
  }, []);

  const cargarPeticiones = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    
    try {
      const [peticionesRes, tecnicosRes] = await Promise.allSettled([
        fetchWithErrorHandling(API_URLS.peticiones.obtener),
        fetchWithErrorHandling(API_URLS.usuarios.obtener)
      ]);

      const peticionesData = peticionesRes.status === 'fulfilled' && Array.isArray(peticionesRes.value) 
        ? peticionesRes.value 
        : [];
      
      const tecnicosData = tecnicosRes.status === 'fulfilled' && Array.isArray(tecnicosRes.value) 
        ? tecnicosRes.value 
        : [];

      // Filtrar solo PENDIENTE y TRABAJANDO
      const peticionesFiltradas = peticionesData.filter((peticion: PeticionType) => {
        const estatusReal = getEstatusReal(peticion.estatus, !!peticion.confirmado);
        return estatusReal === 'PENDIENTE' || estatusReal === 'TRABAJANDO';
      });

      setPeticiones(peticionesFiltradas);
      setTecnicos(tecnicosData);
      setLastUpdate(new Date());

      if (peticionesRes.status === 'rejected' || tecnicosRes.status === 'rejected') {
        toast.current?.show({
          severity: 'warn',
          summary: 'Advertencia',
          detail: 'Algunos datos no se pudieron cargar',
          life: 4000
        });
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudieron cargar las peticiones',
        life: 4000
      });
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [fetchWithErrorHandling]);

  // Cargar datos iniciales
  useEffect(() => {
    cargarPeticiones(true);
  }, [cargarPeticiones]);

  // Configurar polling para actualización automática
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      cargarPeticiones(false);
    }, POLLING_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [cargarPeticiones]);

  // Agrupar peticiones por estatus
  const peticionesPorEstatus = useMemo(() => {
    const grupos: Record<string, PeticionType[]> = {};
    
    peticiones.forEach(peticion => {
      const estatusReal = getEstatusReal(peticion.estatus, !!peticion.confirmado);
      if (ESTATUS_CONFIG[estatusReal as keyof typeof ESTATUS_CONFIG]) {
        if (!grupos[estatusReal]) {
          grupos[estatusReal] = [];
        }
        grupos[estatusReal].push(peticion);
      }
    });

    // Ordenar peticiones dentro de cada grupo por fecha (más recientes primero)
    Object.keys(grupos).forEach(estatus => {
      grupos[estatus].sort((a, b) => 
        new Date(b.fecha_hora).getTime() - new Date(a.fecha_hora).getTime()
      );
    });

    return grupos;
  }, [peticiones]);

  // Obtener técnico asignado
  const getTecnicoInfo = useCallback((peticion: PeticionType): Tecnico | null => {
    if (peticion.tecnicoAsignado) {
      return peticion.tecnicoAsignado;
    }
    
    if (peticion.tecnico_id) {
      return tecnicos.find(t => t.id === peticion.tecnico_id) || null;
    }
    
    return null;
  }, [tecnicos]);

  // Event handlers
  const handleVerDetalle = useCallback((peticion: PeticionType) => {
    setSelectedPeticion(peticion);
    setModalVisible(true);
  }, []);

  const handleVerImagen = useCallback((folio: number) => {
    const imagenUrl = `${API_URLS.peticiones.obtenerPorFolio(folio.toString()).replace('/getPeticionByFolio/', '/getImagenPeticion/')}`;
    setImagenSeleccionada(imagenUrl);
    setImagenModalVisible(true);
  }, []);

  const handleRefresh = useCallback(() => {
    cargarPeticiones(true);
    toast.current?.show({
      severity: 'info',
      summary: 'Actualizado',
      detail: 'Panel actualizado',
      life: 3000
    });
  }, [cargarPeticiones]);

  // Render petición card compacta estilo actividad reciente - MODO OSCURO CON LETRAS MÁS GRANDES
  const renderPeticionCard = useCallback((peticion: PeticionType) => {
    const tecnico = getTecnicoInfo(peticion);
    const estatusReal = getEstatusReal(peticion.estatus, !!peticion.confirmado);
    const config = ESTATUS_CONFIG[estatusReal as keyof typeof ESTATUS_CONFIG];

    return (
      <div key={peticion.folio} className="col-lg-6 col-md-6 col-sm-12 mb-3">
        <div
          className="card h-100 dark-card"
          style={{
            backgroundColor: DARK_COLORS.cardBg,
            border: `2px solid ${config?.color || DARK_COLORS.border}`,
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            borderRadius: '12px',
            minHeight: '240px', // Aumentado ligeramente para acomodar texto más grande
            color: DARK_COLORS.primaryText,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
          }}
          onClick={() => handleVerDetalle(peticion)}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.4)';
            e.currentTarget.style.backgroundColor = DARK_COLORS.hoverBg;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
            e.currentTarget.style.backgroundColor = DARK_COLORS.cardBg;
          }}
        >
          <div className="card-body p-4"> {/* Aumentado padding */}
            <div className="d-flex align-items-start">
              {/* Icono y folio */}
              <div className="mr-3 mt-1">
                <div style={{
                  background: config?.darkGradient || config?.color,
                  color: 'white',
                  fontSize: '1.1rem', // Aumentado de 0.8rem a 1.1rem
                  fontWeight: '900',
                  padding: '10px 14px', // Aumentado padding
                  borderRadius: '12px',
                  textAlign: 'center',
                  minWidth: '60px', // Aumentado de 50px
                  boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
                }}>
                  #{peticion.folio}
                </div>
              </div>
              
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                {/* Título */}
                <div className="font-weight-bold mb-3" style={{ // Aumentado margin-bottom
                  fontSize: '1.2rem', // Aumentado de 0.9rem a 1.2rem
                  lineHeight: '1.4', // Aumentado line-height
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  whiteSpace: 'normal',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  height: '3.4em', // Aumentado para acomodar texto más grande
                  maxWidth: '100%',
                  color: DARK_COLORS.primaryText
                }}>
                  {peticion.titulo}
                </div>

                {/* Sucursal y Departamento */}
                <div className="mb-3" style={{ fontSize: '1rem' }}> {/* Aumentado de 0.75rem a 1rem */}
                  <div className="mb-2" style={{ // Aumentado margin-bottom
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: DARK_COLORS.success,
                    lineHeight: '1.5' // Añadido line-height
                  }}>
                    <i className="pi pi-building mr-2" style={{ fontSize: '1.1rem' }}></i> {/* Aumentado tamaño del icono */}
                    {peticion.sucursal}
                  </div>
                  <div style={{ 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: DARK_COLORS.warning,
                    lineHeight: '1.5'
                  }}>
                    <i className="pi pi-sitemap mr-2" style={{ fontSize: '1.1rem' }}></i>
                    {peticion.departamento}
                  </div>
                </div>

                {/* Técnico */}
                <div className="mb-3" style={{ fontSize: '1rem' }}> {/* Aumentado de 0.8rem a 1rem */}
                  {tecnico ? (
                    <div style={{ 
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: DARK_COLORS.info,
                      lineHeight: '1.5'
                    }}>
                      <i className="pi pi-wrench mr-2" style={{ fontSize: '1.1rem' }}></i>
                      {tecnico.nombre}
                    </div>
                  ) : (
                    <div style={{ 
                      color: DARK_COLORS.danger,
                      lineHeight: '1.5'
                    }}>
                      <i className="pi pi-exclamation-triangle mr-2" style={{ fontSize: '1.1rem' }}></i>
                      Sin técnico
                    </div>
                  )}
                </div>

                {/* Fecha promesa */}
                {peticion.fechapromesa && (
                  <div className="mb-3" style={{ 
                    fontSize: '0.95rem', // Aumentado de 0.75rem a 0.95rem
                    color: DARK_COLORS.secondaryText,
                    lineHeight: '1.5'
                  }}>
                    <i className="pi pi-calendar mr-2" style={{ fontSize: '1rem' }}></i>
                    {new Date(peticion.fechapromesa).toLocaleDateString('es-ES')}
                  </div>
                )}

                {/* Imagen indicator */}
                {peticion.imagen_url && (
                  <div className="mb-3">
                    <Button
                      icon="pi pi-image"
                      className="p-button-rounded p-button-text p-button-sm dark-button"
                      style={{ 
                        color: DARK_COLORS.success,
                        backgroundColor: 'rgba(16, 185, 129, 0.2)',
                        border: `1px solid ${DARK_COLORS.success}`,
                        width: '36px', // Aumentado de 28px
                        height: '36px',
                        fontSize: '1rem' // Aumentado de 0.8rem
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleVerImagen(peticion.folio);
                      }}
                      tooltip="Ver imagen"
                    />
                  </div>
                )}

                {/* Fecha y hora */}
                <div style={{ 
                  fontSize: '0.9rem', // Aumentado de 0.7rem a 0.9rem
                  color: DARK_COLORS.mutedText,
                  lineHeight: '1.5',
                  marginBottom: '12px' // Añadido margin-bottom
                }}>
                  <i className="pi pi-clock mr-2" style={{ fontSize: '1rem' }}></i>
                  {formatDateTime(peticion.fecha_hora)}
                </div>

                {/* Indicador de clic */}
                <div className="mt-2" style={{ 
                  fontSize: '0.85rem', // Aumentado de 0.65rem a 0.85rem
                  color: DARK_COLORS.accent,
                  lineHeight: '1.4'
                }}>
                  <i className="pi pi-eye mr-2" style={{ fontSize: '0.9rem' }}></i>
                  Clic para ver detalles
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }, [getTecnicoInfo, handleVerDetalle, handleVerImagen]);

  // Render columna de estatus optimizada para modo oscuro
  const renderEstatusColumn = useCallback((estatus: string, peticiones: PeticionType[]) => {
    const config = ESTATUS_CONFIG[estatus as keyof typeof ESTATUS_CONFIG];
    
    return (
      <div
        key={estatus}
        style={{
          flex: '1',
          minWidth: '100%',
          background: `linear-gradient(145deg, ${DARK_COLORS.secondaryBg} 0%, ${DARK_COLORS.cardBg} 100%)`,
          borderRadius: '24px',
          padding: '24px',
          boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.5)',
          border: `2px solid ${DARK_COLORS.border}`,
          backdropFilter: 'blur(15px)',
          margin: '0 0 24px 0'
        }}
      >
        {/* Header de la columna */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '24px',
          padding: '20px 24px',
          background: config?.darkGradient || config?.color,
          borderRadius: '20px',
          color: 'white',
          boxShadow: '0 12px 28px -6px rgba(0,0,0,0.4)',
          minHeight: `${MIN_TOUCH_TARGET + 16}px`
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <span style={{ fontSize: '2rem' }}>{config?.icon || '📋'}</span>
            <h2 style={{
              margin: 0,
              fontSize: '1.6rem',
              fontWeight: '900',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}>
              {config?.label || estatus}
            </h2>
          </div>
          <div style={{
            background: 'rgba(255, 255, 255, 0.2)',
            color: 'white',
            fontWeight: '900',
            padding: '12px 20px',
            borderRadius: '24px',
            fontSize: '1.4rem',
            boxShadow: '0 6px 16px rgba(0,0,0,0.3)',
            backdropFilter: 'blur(10px)',
            minWidth: '60px',
            textAlign: 'center'
          }}>
            {peticiones.length}
          </div>
        </div>

        {/* Grid de peticiones */}
        <div style={{
          maxHeight: '75vh',
          overflowY: 'auto',
          paddingRight: '12px'
        }}>
          {peticiones.length > 0 ? (
            <div className="row">
              {peticiones.map(renderPeticionCard)}
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: DARK_COLORS.mutedText,
              fontStyle: 'italic'
            }}>
              <i className="pi pi-inbox" style={{ 
                fontSize: '3.5rem', 
                marginBottom: '16px', 
                display: 'block',
                color: DARK_COLORS.secondaryText
              }} />
              <div style={{ 
                fontSize: '1.3rem', 
                fontWeight: '700',
                color: DARK_COLORS.secondaryText
              }}>
                No hay peticiones
              </div>
              <div style={{ 
                fontSize: '1rem', 
                marginTop: '8px',
                color: DARK_COLORS.mutedText
              }}>
                en este estado
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }, [renderPeticionCard]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: `linear-gradient(135deg, ${DARK_COLORS.primaryBg} 0%, ${DARK_COLORS.secondaryBg} 100%)`
      }}>
        <div style={{ 
          textAlign: 'center',
          background: DARK_COLORS.cardBg,
          padding: '60px',
          borderRadius: '28px',
          boxShadow: '0 24px 48px rgba(0,0,0,0.4)',
          backdropFilter: 'blur(15px)',
          border: `1px solid ${DARK_COLORS.border}`
        }}>
          <i className="pi pi-spinner pi-spin" style={{ 
            fontSize: '4rem', 
            color: DARK_COLORS.accent,
            marginBottom: '24px',
            display: 'block'
          }}></i>
          <div style={{ 
            fontSize: '1.6rem',
            color: DARK_COLORS.primaryText,
            fontWeight: '800'
          }}>
            Cargando panel...
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Toast ref={toast} />
      
      <div style={{
        background: `linear-gradient(135deg, ${DARK_COLORS.primaryBg} 0%, ${DARK_COLORS.secondaryBg} 100%)`,
        minHeight: '100vh',
        padding: '24px'
      }}>
        {/* Panel de columnas */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0px',
          overflowX: 'auto',
          paddingBottom: '24px'
        }}>
          {Object.entries(ESTATUS_CONFIG)
            .sort(([, a], [, b]) => a.order - b.order)
            .map(([estatus]) => 
              renderEstatusColumn(estatus, peticionesPorEstatus[estatus] || [])
            )}
        </div>
      </div>

      {/* Modal de detalle optimizado para modo oscuro */}
      <Dialog
        header={`Petición #${selectedPeticion?.folio || ''}`}
        visible={modalVisible}
        style={{ width: '90vw', maxWidth: '1000px' }}
        onHide={() => setModalVisible(false)}
        headerStyle={{ 
          fontSize: '1.8rem', 
          fontWeight: '800', 
          padding: '28px',
          backgroundColor: DARK_COLORS.modalBg,
          color: DARK_COLORS.primaryText,
          borderBottom: `1px solid ${DARK_COLORS.border}`
        }}
        contentStyle={{ 
          padding: '0',
          backgroundColor: DARK_COLORS.modalBg,
          color: DARK_COLORS.primaryText
        }}
        className="dark-modal"
      >
        {selectedPeticion && (
          <div style={{ padding: '28px', fontSize: '1.3rem' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '24px',
              marginBottom: '28px'
            }}>
              <div style={{
                padding: '20px',
                backgroundColor: DARK_COLORS.elementBg,
                borderRadius: '16px',
                border: `2px solid ${DARK_COLORS.border}`,
                minHeight: `${MIN_TOUCH_TARGET + 16}px`
              }}>
                <strong style={{ color: DARK_COLORS.primaryText, fontSize: '1.4rem' }}>📅 Fecha y Hora:</strong>
                <div style={{ 
                  marginTop: '10px', 
                  fontWeight: '800', 
                  fontSize: '1.3rem',
                  color: DARK_COLORS.accent
                }}>
                  {formatDateTime(selectedPeticion.fecha_hora)}
                </div>
              </div>
              <div style={{
                padding: '20px',
                backgroundColor: DARK_COLORS.elementBg,
                borderRadius: '16px',
                border: `2px solid ${DARK_COLORS.border}`,
                minHeight: `${MIN_TOUCH_TARGET + 16}px`
              }}>
                <strong style={{ color: DARK_COLORS.primaryText, fontSize: '1.4rem' }}>👤 Empleado:</strong>
                <div style={{ 
                  marginTop: '10px', 
                  fontWeight: '800', 
                  fontSize: '1.3rem',
                  color: DARK_COLORS.info
                }}>
                  {selectedPeticion.empleado?.nomemp || 'N/A'}
                </div>
              </div>
              <div style={{
                padding: '20px',
                backgroundColor: DARK_COLORS.elementBg,
                borderRadius: '16px',
                border: `2px solid ${DARK_COLORS.border}`,
                minHeight: `${MIN_TOUCH_TARGET + 16}px`
              }}>
                <strong style={{ color: DARK_COLORS.primaryText, fontSize: '1.4rem' }}>🏢 Sucursal:</strong>
                <div style={{ 
                  marginTop: '10px', 
                  fontWeight: '800', 
                  fontSize: '1.3rem',
                  color: DARK_COLORS.success
                }}>
                  {selectedPeticion.sucursal}
                </div>
              </div>
              <div style={{
                padding: '20px',
                backgroundColor: DARK_COLORS.elementBg,
                borderRadius: '16px',
                border: `2px solid ${DARK_COLORS.border}`,
                minHeight: `${MIN_TOUCH_TARGET + 16}px`
              }}>
                <strong style={{ color: DARK_COLORS.primaryText, fontSize: '1.4rem' }}>🏬 Departamento:</strong>
                <div style={{ 
                  marginTop: '10px', 
                  fontWeight: '800', 
                  fontSize: '1.3rem',
                  color: DARK_COLORS.warning
                }}>
                  {selectedPeticion.departamento}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <strong style={{ color: DARK_COLORS.primaryText, fontSize: '1.4rem' }}>📌 Título:</strong>
              <div style={{ 
                marginTop: '12px',
                fontSize: '1.5rem',
                fontWeight: '800',
                color: DARK_COLORS.accent
              }}>
                {selectedPeticion.titulo}
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <strong style={{ color: DARK_COLORS.primaryText, fontSize: '1.4rem' }}>📝 Justificación:</strong>
              <div style={{ 
                marginTop: '12px',
                padding: '20px',
                backgroundColor: DARK_COLORS.elementBg,
                borderRadius: '16px',
                border: `2px solid ${DARK_COLORS.border}`,
                lineHeight: '1.6',
                fontSize: '1.3rem',
                color: DARK_COLORS.secondaryText
              }}>
                {selectedPeticion.justificacion}
              </div>
            </div>

            {selectedPeticion.motivo && (
              <div style={{ marginBottom: '24px' }}>
                <strong style={{ color: DARK_COLORS.primaryText, fontSize: '1.4rem' }}>💬 Motivo:</strong>
                <div style={{ 
                  marginTop: '12px',
                  padding: '20px',
                  backgroundColor: 'rgba(245, 158, 11, 0.1)',
                  borderRadius: '16px',
                  border: `2px solid ${DARK_COLORS.warning}`,
                  lineHeight: '1.6',
                  fontSize: '1.3rem',
                  color: DARK_COLORS.warning
                }}>
                  {selectedPeticion.motivo}
                </div>
              </div>
            )}

            {selectedPeticion.fechapromesa && (
              <div style={{ marginBottom: '24px' }}>
                <strong style={{ color: DARK_COLORS.primaryText, fontSize: '1.4rem' }}>📅 Fecha Promesa:</strong>
                <div style={{ 
                  marginTop: '12px',
                  fontWeight: '800',
                  color: DARK_COLORS.accent,
                  fontSize: '1.3rem'
                }}>
                  {new Date(selectedPeticion.fechapromesa).toLocaleDateString('es-ES')}
                </div>
              </div>
            )}

            {selectedPeticion.imagen_url && (
              <div style={{ marginBottom: '24px', textAlign: 'center' }}>
                <strong style={{ color: DARK_COLORS.primaryText, fontSize: '1.4rem' }}>🖼️ Imagen:</strong>
                <div style={{ marginTop: '16px' }}>
                  <img
                    src={`${API_URLS.peticiones.obtenerPorFolio(selectedPeticion.folio.toString()).replace('/getPeticionByFolio/', '/getImagenPeticion/')}`}
                    alt="Imagen de la petición"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '350px',
                      borderRadius: '16px',
                      boxShadow: '0 12px 28px -6px rgba(0, 0, 0, 0.4)',
                      cursor: 'pointer',
                      border: `2px solid ${DARK_COLORS.border}`
                    }}
                    onClick={() => handleVerImagen(selectedPeticion.folio)}
                    onError={(e) => {
                      e.currentTarget.src = '/dist/img/no-image.png';
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </Dialog>

      {/* Modal de imagen optimizado para modo oscuro */}
      <Dialog
        header="Imagen de la Petición"
        visible={imagenModalVisible}
        style={{ width: '95vw', maxWidth: '1200px' }}
        onHide={() => setImagenModalVisible(false)}
        headerStyle={{ 
          fontSize: '1.8rem', 
          fontWeight: '800', 
          padding: '28px',
          backgroundColor: DARK_COLORS.modalBg,
          color: DARK_COLORS.primaryText,
          borderBottom: `1px solid ${DARK_COLORS.border}`
        }}
        contentStyle={{
          backgroundColor: DARK_COLORS.modalBg,
          color: DARK_COLORS.primaryText
        }}
        className="dark-modal"
      >
        <div style={{ textAlign: 'center', padding: '16px' }}>
          <img
            src={imagenSeleccionada}
            alt="Imagen de la petición"
            style={{
              maxWidth: '100%',
              maxHeight: '70vh',
              borderRadius: '16px',
              boxShadow: '0 12px 28px -6px rgba(0, 0, 0, 0.4)',
              border: `2px solid ${DARK_COLORS.border}`
            }}
            onError={(e) => {
              e.currentTarget.src = '/dist/img/no-image.png';
            }}
          />
        </div>
      </Dialog>

      {/* Estilos CSS optimizados para modo oscuro */}
      <style>
        {`
          /* MODO OSCURO GLOBAL */
          body {
            background-color: ${DARK_COLORS.primaryBg} !important;
            color: ${DARK_COLORS.primaryText} !important;
          }

          /* Tarjetas en modo oscuro */
          .dark-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4) !important;
            background-color: ${DARK_COLORS.hoverBg} !important;
          }

          .dark-card:active {
            transform: translateY(-1px);
            transition: all 0.1s ease;
          }

          /* Scrollbar en modo oscuro */
          ::-webkit-scrollbar {
            width: 16px;
            height: 16px;
          }

          ::-webkit-scrollbar-track {
            background: ${DARK_COLORS.elementBg};
            border-radius: 8px;
          }

          ::-webkit-scrollbar-thumb {
            background: linear-gradient(135deg, ${DARK_COLORS.accent} 0%, ${DARK_COLORS.accentHover} 100%);
            border-radius: 8px;
            border: 2px solid ${DARK_COLORS.elementBg};
            min-height: 48px;
          }

          ::-webkit-scrollbar-thumb:hover {
            background: linear-gradient(135deg, ${DARK_COLORS.accentHover} 0%, #3730a3 100%);
          }

          /* Botones en modo oscuro */
          .p-button {
            min-height: ${MIN_TOUCH_TARGET + 16}px !important;
            font-size: 1.2rem !important;
            font-weight: 800 !important;
            padding: 14px 20px !important;
            background-color: ${DARK_COLORS.accent} !important;
            border-color: ${DARK_COLORS.accent} !important;
            color: white !important;
          }

          .p-button:hover {
            background-color: ${DARK_COLORS.accentHover} !important;
            border-color: ${DARK_COLORS.accentHover} !important;
          }

          .dark-button {
            background-color: rgba(16, 185, 129, 0.2) !important;
            border-color: ${DARK_COLORS.success} !important;
            color: ${DARK_COLORS.success} !important;
          }

          .dark-button:hover {
            background-color: rgba(16, 185, 129, 0.3) !important;
            transform: scale(1.05);
          }

          .p-button-rounded {
            min-width: ${MIN_TOUCH_TARGET + 8}px !important;
            min-height: ${MIN_TOUCH_TARGET + 8}px !important;
          }

          .p-button-sm {
            min-height: 36px !important;
            min-width: 36px !important;
            padding: 6px !important;
          }

          /* Modal en modo oscuro */
          .dark-modal .p-dialog {
            background-color: ${DARK_COLORS.modalBg} !important;
            border: 2px solid ${DARK_COLORS.border} !important;
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.6) !important;
          }

          .dark-modal .p-dialog-header {
            background-color: ${DARK_COLORS.modalBg} !important;
            color: ${DARK_COLORS.primaryText} !important;
            border-bottom: 1px solid ${DARK_COLORS.border} !important;
          }

          .dark-modal .p-dialog-content {
            background-color: ${DARK_COLORS.modalBg} !important;
            color: ${DARK_COLORS.primaryText} !important;
          }

          .dark-modal .p-dialog-header-icon {
            width: ${MIN_TOUCH_TARGET}px !important;
            height: ${MIN_TOUCH_TARGET}px !important;
            font-size: 1.3rem !important;
            color: ${DARK_COLORS.primaryText} !important;
            background-color: ${DARK_COLORS.elementBg} !important;
            border: 1px solid ${DARK_COLORS.border} !important;
          }

          .dark-modal .p-dialog-header-icon:hover {
            background-color: ${DARK_COLORS.hoverBg} !important;
            color: ${DARK_COLORS.danger} !important;
          }

          /* Toast en modo oscuro */
          .p-toast .p-toast-message {
            font-size: 1.3rem !important;
            padding: 20px !important;
            border-radius: 14px !important;
            background-color: ${DARK_COLORS.cardBg} !important;
            border: 1px solid ${DARK_COLORS.border} !important;
            color: ${DARK_COLORS.primaryText} !important;
            box-shadow: 0 10px 25px rgba(0, 0, 0, 0.4) !important;
          }

          .p-toast .p-toast-icon-close {
            width: ${MIN_TOUCH_TARGET}px !important;
            height: ${MIN_TOUCH_TARGET}px !important;
            font-size: 1.2rem !important;
            color: ${DARK_COLORS.primaryText} !important;
          }

          .p-toast .p-toast-icon-close:hover {
            background-color: ${DARK_COLORS.hoverBg} !important;
          }

          /* Animaciones suaves */
          @keyframes slideInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .dark-card {
            animation: slideInUp 0.4s ease-out;
          }

          /* Optimización para táctil */
          * {
            -webkit-touch-callout: none;
            -webkit-user-select: none;
            -khtml-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
            user-select: none;
          }
          
          *:focus {
            outline: 3px solid ${DARK_COLORS.accent};
            outline-offset: 2px;
          }

          /* Responsive para diferentes tamaños táctiles */
          @media (max-width: 1200px) {
            .col-lg-6 {
              flex: 0 0 50%;
              max-width: 50%;
            }
          }

          @media (max-width: 992px) {
            .col-lg-6 {
              flex: 0 0 50%;
              max-width: 50%;
            }
          }

          @media (max-width: 576px) {
            .col-lg-6 {
              flex: 0 0 100%;
              max-width: 100%;
            }
          }

          /* Mejoras para accesibilidad táctil */
          button:focus,
          .p-button:focus {
            box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.5) !important;
          }

          /* Espaciado mejorado para elementos interactivos */
          .p-dialog .p-dialog-header-icons {
            gap: 12px;
          }

          /* Efectos de hover mejorados */
          .dark-card {
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .dark-card:hover {
            transform: translateY(-4px) scale(1.02);
          }

          /* Gradientes personalizados para elementos */
          .gradient-bg {
            background: linear-gradient(135deg, ${DARK_COLORS.accent} 0%, ${DARK_COLORS.accentHover} 100%);
          }

          /* Sombras mejoradas */
          .enhanced-shadow {
            box-shadow: 
              0 4px 6px -1px rgba(0, 0, 0, 0.1),
              0 2px 4px -1px rgba(0, 0, 0, 0.06),
              0 0 0 1px rgba(255, 255, 255, 0.05);
          }

          /* Animación de carga */
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }

          .loading-pulse {
            animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          }
        `}
      </style>
    </>
  );
};

export default PeticionesPanel;